import { type ClassValue, clsx } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * Formata uma data para exibição em português brasileiro sem problemas de timezone
 * @param dateString - String da data (pode ser ISO string ou data local)
 * @returns Data formatada em pt-BR
 */
export function formatDateBR(dateString: string | Date): string {
  if (!dateString) return ''
  
  const date = new Date(dateString)
  
  // Se a string termina com 'Z', é UTC e precisamos ajustar para timezone local
  if (typeof dateString === 'string' && dateString.endsWith('Z')) {
    // Extrai apenas a parte da data (YYYY-MM-DD) para evitar conversão de timezone
    const dateOnly = dateString.split('T')[0]
    const [year, month, day] = dateOnly.split('-').map(Number)
    const localDate = new Date(year, month - 1, day) // month is 0-indexed
    return localDate.toLocaleDateString('pt-BR')
  }
  
  return date.toLocaleDateString('pt-BR')
}

/**
 * Formata uma data e hora para exibição em português brasileiro com timezone de Brasília
 * @param dateString - String da data/hora (pode ser ISO string ou data local)
 * @returns Data e hora formatada em pt-BR no timezone de Brasília
 */
export function formatDateTimeBR(dateString: string | Date): string {
  if (!dateString) return ''
  
  const date = new Date(dateString)
  
  return date.toLocaleString('pt-BR', {
    timeZone: 'America/Sao_Paulo',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  })
}

/**
 * Converte uma data para formato de input HTML (YYYY-MM-DD) SEM problemas de timezone
 * Esta função garante que a data selecionada pelo usuário seja exatamente a data retornada
 * @param dateInput - String da data ou objeto Date
 * @returns Data no formato YYYY-MM-DD para inputs HTML
 */
export function toInputDateFormat(dateInput: string | Date): string {
  if (!dateInput) return ''
  
  // Se é uma string ISO UTC, extrai apenas a parte da data
  if (typeof dateInput === 'string' && dateInput.includes('T')) {
    return dateInput.split('T')[0]
  }
  
  // Se é um objeto Date, usa os métodos getFullYear, getMonth, getDate
  // que são timezone-safe (sempre retornam valores baseados no timezone local)
  if (dateInput instanceof Date) {
    const year = dateInput.getFullYear()
    const month = String(dateInput.getMonth() + 1).padStart(2, '0')
    const day = String(dateInput.getDate()).padStart(2, '0')
    
    return `${year}-${month}-${day}`
  }
  
  // Se é uma string de data simples (YYYY-MM-DD ou similar)
  if (typeof dateInput === 'string') {
    // Se já está no formato correto, retorna diretamente
    if (/^\d{4}-\d{2}-\d{2}$/.test(dateInput)) {
      return dateInput
    }
    
    // Caso contrário, converte para Date e usa os métodos locais
    const date = new Date(dateInput)
    
    // Verifica se é uma data válida
    if (isNaN(date.getTime())) {
      return ''
    }
    
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    
    return `${year}-${month}-${day}`
  }
  
  return ''
}

/**
 * Função específica para lidar com datas vindas de date pickers
 * Cria um objeto Date na timezone local para evitar problemas de conversão
 * @param date - Objeto Date do date picker
 * @returns String no formato YYYY-MM-DD
 */
export function datePickerToString(date: Date | null | undefined): string {
  if (!date) return ''
  
  // Cria uma nova data na timezone local usando os valores do date picker
  // Isso evita problemas de timezone ao converter para string
  const localDate = new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate()
  )
  
  const year = localDate.getFullYear()
  const month = String(localDate.getMonth() + 1).padStart(2, '0')
  const day = String(localDate.getDate()).padStart(2, '0')
  
  return `${year}-${month}-${day}`
}

/**
 * Função para converter string de data para objeto Date na timezone local
 * @param dateString - String no formato YYYY-MM-DD
 * @returns Objeto Date ou undefined
 */
export function stringToDatePicker(dateString: string): Date | undefined {
  if (!dateString) return undefined
  
  // Parse da string de data
  const parts = dateString.split('-')
  if (parts.length !== 3) return undefined
  
  const year = parseInt(parts[0], 10)
  const month = parseInt(parts[1], 10) - 1 // Month is 0-indexed
  const day = parseInt(parts[2], 10)
  
  // Verifica se os valores são válidos
  if (isNaN(year) || isNaN(month) || isNaN(day)) return undefined
  
  // Cria um objeto Date na timezone local
  return new Date(year, month, day)
} 

/**
 * Verifica se um usuário tem permissões de administrador
 * @param profile - Perfil do usuário ou undefined
 * @returns true se o usuário é admin, false caso contrário
 */
export function isAdmin(profile?: { role: string } | null): boolean {
  return profile?.role === 'ADMIN'
}

/**
 * Verifica se um usuário tem permissões de administrador e lança erro se não tiver
 * Útil para APIs que precisam de validação de admin
 * @param profile - Perfil do usuário ou undefined
 * @throws Error se o usuário não for admin
 */
export function requireAdmin(profile?: { role: string } | null): void {
  if (!isAdmin(profile)) {
    throw new Error('Acesso negado. Apenas administradores podem acessar este recurso.')
  }
}

/**
 * Resposta padronizada para acesso negado
 * @returns Response de erro 403
 */
export function createAdminOnlyResponse() {
  return new Response(
    JSON.stringify({ 
      error: 'Acesso negado',
      message: 'Apenas administradores podem acessar este recurso.'
    }),
    { 
      status: 403,
      headers: {
        'Content-Type': 'application/json'
      }
    }
  )
}

/**
 * Cria uma resposta de erro padronizada para APIs
 * @param message - Mensagem de erro user-friendly
 * @param status - Status code HTTP (padrão 500)
 * @param details - Detalhes técnicos do erro (opcional)
 * @returns Response de erro padronizada
 */
export function createErrorResponse(message: string, status: number = 500, details?: string) {
  return new Response(
    JSON.stringify({ 
      error: 'Erro interno',
      message,
      details: process.env.NODE_ENV === 'development' ? details : undefined
    }),
    { 
      status,
      headers: {
        'Content-Type': 'application/json'
      }
    }
  )
}

/**
 * Função para lidar com erros de forma user-friendly
 * @param error - Erro capturado
 * @param fallbackMessage - Mensagem padrão se não conseguir extrair do erro
 * @returns Mensagem de erro user-friendly
 */
export function getErrorMessage(error: any, fallbackMessage: string = 'Ocorreu um erro inesperado'): string {
  if (typeof error === 'string') return error
  if (error?.message) return error.message
  if (error?.error) return error.error
  return fallbackMessage
} 